import React from 'react';

const ConfirmationDialog = ({
  open,
  onClose,
  formData,
  getLocationDisplay,
  getEquipmentDisplay,
  formatDate,
  onConfirm,
  isSubmitting
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden w-full max-w-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="bg-gray-50 border-b-2 border-gray-200 p-4 sm:p-6 flex items-center gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-lg sm:rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-black" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-black font-semibold text-lg sm:text-xl font-poppins">
            Review Your Request
          </h2>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          <div className="flex flex-col gap-4">
            {/* Personal Information Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-black mb-3 font-poppins flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                Personal Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 font-poppins mb-1">Name</p>
                  <p className="font-medium font-poppins">{formData.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-poppins mb-1">Email</p>
                  <p className="font-medium font-poppins">{formData.email}</p>
                </div>
              </div>
            </div>

            {/* Location Information Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-black mb-3 font-poppins flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                Location Details
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <p className="text-sm text-gray-600 font-poppins mb-1">Location</p>
                  <p className="font-medium font-poppins">{getLocationDisplay()}</p>
                </div>
                {formData.locationType === 'SDO' && (
                  <div>
                    <p className="text-sm text-gray-600 font-poppins mb-1">Department</p>
                    <p className="font-medium font-poppins">{formData.department}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Equipment Details Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-black mb-3 font-poppins flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                Equipment Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 font-poppins mb-1">Equipment</p>
                  <p className="font-medium font-poppins">{getEquipmentDisplay()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-poppins mb-1">Serial No.</p>
                  <p className="font-medium font-poppins">{formData.serialNo}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-poppins mb-1">Date of Request</p>
                  <p className="font-medium font-poppins">{formatDate(formData.dateOfRequest)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-poppins mb-1">Priority</p>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium font-poppins ${
                    formData.priority === 'HIGH' ? 'bg-red-100 text-red-800' :
                    formData.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {formData.priority}
                  </span>
                </div>
              </div>
            </div>

            {/* Problem Description Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-black mb-3 font-poppins flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                Problem Description
              </h3>
              {formData.specificProblem ? (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="whitespace-pre-wrap leading-relaxed font-poppins">
                    {formData.specificProblem}
                  </p>
                </div>
              ) : (
                <p className="text-gray-600 font-poppins text-sm">
                  No problem description provided
                </p>
              )}
            </div>

            {/* Attachment Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-black mb-3 font-poppins flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.31.083l-3-2.5a1 1 0 011.122-1.65l1.877.626z" clipRule="evenodd" />
                </svg>
                Attachment
              </h3>
              {formData.file ? (
                <div className="flex items-center gap-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.31.083l-3-2.5a1 1 0 011.122-1.65l1.877.626z" clipRule="evenodd" />
                  </svg>
                  <p className="font-medium font-poppins">{formData.file.name}</p>
                </div>
              ) : (
                <p className="text-gray-600 font-poppins text-sm">
                  No file attached
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-gray-50 border-t border-gray-200 p-4 sm:p-6 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            className="flex-1 sm:flex-none px-4 py-2 border border-black text-black rounded-lg font-poppins hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Edit Details
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex-1 sm:flex-none px-6 py-2 bg-black text-white rounded-lg font-poppins hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Confirm & Submit
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;